/**
 * Utility functions for date formatting
 */

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localeData from 'dayjs/plugin/localeData';

// Extend dayjs with plugins
dayjs.extend(advancedFormat);
dayjs.extend(localeData);

/**
 * Detect user's preferred date format based on browser locale
 * Returns 'DD-MM-YYYY' or 'MM-DD-YYYY'
 */
export const getUserDateFormat = (): 'DD-MM-YYYY' | 'MM-DD-YYYY' => {
  try {
    // Get browser locale
    const locale = navigator.language || 'en-GB';

    // Test date: January 2, 2000 (to clearly distinguish MM vs DD)
    const testDate = new Date(2000, 0, 2); // Month is 0-indexed
    const formatted = testDate.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Check if the format starts with 01 (month) or 02 (day)
    if (formatted.startsWith('01')) {
      return 'MM-DD-YYYY'; // US format
    } else {
      return 'DD-MM-YYYY'; // Most other locales
    }
  } catch {
    // Default to DD-MM-YYYY if detection fails
    return 'DD-MM-YYYY';
  }
};

/**
 * Get the input date format pattern for the user's locale
 */
export const getDateInputFormat = (): string => {
  return getUserDateFormat();
};

/**
 * Get the input date placeholder for the user's locale
 */
export const getDateInputPlaceholder = (label?: string): string => {
  const format = getUserDateFormat();
  return label ? `${label} (${format})` : format;
};

/**
 * Format date for display with ordinal suffix (e.g., "January 25th, 2025")
 * Used for read-only date displays like course deadlines
 */
export const formatDateDisplay = (date?: Date | number | string | null): string => {
  if (!date) return 'Not specified';

  try {
    let dateObj: dayjs.Dayjs;

    if (typeof date === 'number') {
      // Assume seconds, convert to milliseconds
      dateObj = dayjs(date * 1000);
    } else {
      dateObj = dayjs(date);
    }

    if (!dateObj.isValid()) return 'Invalid date';

    // Format: "January 25th, 2025"
    return dateObj.format('MMMM Do, YYYY');
  } catch (error) {
    console.error('Error formatting date for display:', error, date);
    return 'Invalid date';
  }
};

/**
 * Format date for short display (e.g., "Jan 25, 2025")
 */
export const formatDateShort = (date?: Date | number | string | null): string => {
  if (!date) return 'Not specified';

  try {
    let dateObj: dayjs.Dayjs;

    if (typeof date === 'number') {
      dateObj = dayjs(date * 1000);
    } else {
      dateObj = dayjs(date);
    }

    if (!dateObj.isValid()) return 'Invalid date';

    return dateObj.format('MMM D, YYYY');
  } catch (error) {
    console.error('Error formatting date short:', error, date);
    return 'Invalid date';
  }
};

/**
 * Format date for input fields based on user locale
 */
export const formatDateForInput = (date?: Date | number | string | null): string => {
  if (!date) return '';

  try {
    let dateObj: dayjs.Dayjs;

    if (typeof date === 'number') {
      dateObj = dayjs(date * 1000);
    } else {
      dateObj = dayjs(date);
    }

    if (!dateObj.isValid()) return '';

    const format = getUserDateFormat();
    return format === 'MM-DD-YYYY'
      ? dateObj.format('MM-DD-YYYY')
      : dateObj.format('DD-MM-YYYY');
  } catch (error) {
    console.error('Error formatting date for input:', error, date);
    return '';
  }
};

/**
 * Adjust deadline year based on intended start year
 * If deadline is before September (months 0-8), use same year as start year
 * If deadline is September or later (months 9-11), use year before start year
 */
export const adjustDeadlineYear = (
  deadline: Date | string | number | null | undefined,
  intendedStartYear: number
): Date | null => {
  if (!deadline) return deadline ? new Date(deadline) : null;

  try {
    let dateObj: dayjs.Dayjs;

    if (typeof deadline === 'number') {
      dateObj = dayjs(deadline * 1000);
    } else {
      dateObj = dayjs(deadline);
    }

    if (!dateObj.isValid()) return null;

    // Get the month (0-11)
    const month = dateObj.month();

    // If deadline is before September (months 0-8), use same year as start year
    // If deadline is September or later (months 9-11), use year before start year
    const adjustedYear = month < 9 ? intendedStartYear : intendedStartYear - 1;
    return dateObj.year(adjustedYear).toDate();
  } catch (error) {
    console.error('Error adjusting deadline year:', error, deadline);
    return null;
  }
};

/**
 * Format the last activity time relative to current time
 */
export const formatLastActivityTime = (updatedAt: Date): string => {
  const activityDate = new Date(updatedAt);
  const now = new Date();
  const diffHours = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60 * 60));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return activityDate.toLocaleDateString();
};

/**
 * Get the start of day for a given date in local timezone
 */
const getStartOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get relative time group for a date using calendar days in local timezone
 */
export const getDateGroup = (date: Date): 'Today' | 'Yesterday' | 'Past Week' | 'Past Month' | 'Older' => {
  const sessionDate = new Date(date);
  const now = new Date();

  const todayStart = getStartOfDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const weekAgoStart = new Date(todayStart);
  weekAgoStart.setDate(weekAgoStart.getDate() - 7);

  const monthAgoStart = new Date(todayStart);
  monthAgoStart.setDate(monthAgoStart.getDate() - 30);

  // Check if session is today (from 00:00:00 today onwards)
  if (sessionDate >= todayStart) {
    return 'Today';
  }

  // Check if session is yesterday (from 00:00:00 yesterday to 23:59:59 yesterday)
  if (sessionDate >= yesterdayStart) {
    return 'Yesterday';
  }

  // Check if session is within past week (from 7 days ago to start of yesterday)
  if (sessionDate >= weekAgoStart) {
    return 'Past Week';
  }

  // Check if session is within past month (from 30 days ago to start of past week)
  if (sessionDate >= monthAgoStart) {
    return 'Past Month';
  }

  // Anything older than 30 days
  return 'Older';
};

/**
 * Group order for sorting
 */
const GROUP_ORDER: Record<string, number> = {
  'Today': 0,
  'Yesterday': 1,
  'Past Week': 2,
  'Past Month': 3,
  'Older': 4,
};

/**
 * Group sessions by date
 */
export const groupSessionsByDate = <T extends { updatedAt: Date }>(
  sessions: T[]
): Array<{ group: string; sessions: T[] }> => {
  const grouped = new Map<string, T[]>();

  // Group sessions
  sessions.forEach((session) => {
    const group = getDateGroup(session.updatedAt);
    if (!grouped.has(group)) {
      grouped.set(group, []);
    }
    grouped.get(group)!.push(session);
  });

  // Convert to array and sort by group order
  const result = Array.from(grouped.entries())
    .map(([group, sessions]) => ({
      group,
      sessions: sessions.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()),
    }))
    .sort((a, b) => (GROUP_ORDER[a.group] ?? 999) - (GROUP_ORDER[b.group] ?? 999));

  return result;
};
